import React, { memo, useContext, useMemo } from "react";

// components
import { PaymentAlert, PaymentMaxRetriesAlert, PaymentMethod, PaymentMethodMeta } from "@swiggy-private/payment-ui";

// contexts
import {
    AnalyticsContext,
    CheckoutActionTypes,
    CheckoutContext,
    GlobalHeaderState,
    NEWRELIC_TABLES,
    recordNREvent,
    SwgyHeaderKeysList,
    clearCart,
} from "@swiggy-private/checkout-core";
import {
    ACTION_TYPES,
    NavigateAction,
    NavigationScreenLinkTypes,
    OnPayExtraParams,
    OrderInterruptionAction,
    PaymentAlertActionProps,
} from "@swiggy-private/payment-types";
import { useEffectOnce } from "@swiggy-private/core-ui";

// contexts
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";

// types
import { OrderInterruptionAlertPayload } from "@payment-client/interfaces/Checkout";

// helpers
import { isDuplicateOrder } from "@payment-client/helpers/OrderInterruptionHelper";
import { getBusinessLine, isCategoryInstamart } from "@payment-client/helpers/Misc";

// analytics
import {
    AdditionalDuplicateOrderClickParams,
    duplicateOrderDialogImpressionEvent,
    maxPaymentRetriesAutoPayImpressionEvent,
    maxPaymentRetriesAutoPaySoftFailureImpressionEvent,
    pastPaymentPendingAutoPayImpressionEvent,
    surgeChangeOrderDialogImpressionEvent,
} from "@payment-client/analytics/events/OrderInterruption";
import { getCartContextForAnalytics } from "@payment-client/analytics/helpers";

// constants
import {
    CARD_RATE_LIMIT_EXCEEDED_STATUS_CODE,
    ORDER_TERMINAL_STATUS_CODE,
    PAST_PAYMENT_PENDING_STATUS_CODE,
    DINEOUT_BILL_PAYMENT_INVALID_QR_STATUS_CODE,
    PAYMENT_MAX_RETRIES_EXCEEDED_SOFT_FAILURE_STATUS_CODE,
    PAYMENT_MAX_RETRIES_EXCEEDED_STATUS_CODE,
} from "@payment-client/constants/StatusCodes";
import { useOrderInterruptionAlertAction } from "@payment-client/hooks/UseOrderInterruptionAlertAction";
import { IMPRESSION_ACTIONS } from "@payment-client/analytics/constants";
import { terminalStateAlertScreenviewEvent } from "@payment-client/analytics/events/OrderTerminalState";

interface OrderInterruptionAlertProps {
    payAction: (paymentMethodData: PaymentMethod<PaymentMethodMeta>, _extraParams?: OnPayExtraParams) => void;
    payload: OrderInterruptionAlertPayload;
}

function OrderInterruptionAlert(props: OrderInterruptionAlertProps): React.ReactElement | null {
    const { checkoutState } = useContext(CheckoutContext);
    const { impressionEvent, screenViewEvent } = useContext(AnalyticsContext);

    const { handleActionClick, closeHalfCard, getActionSubtext } = useOrderInterruptionAlertAction(props);

    const { billTotal, amountToPayWithoutCouponWithoutSplitpay } = useContext(PaymentPayContext);
    const { orderInterruptionAlertData, errorStatusCode, errorCodes } = props.payload;
    const { title, subtitle, message, actions, icon } = orderInterruptionAlertData;
    const isDuplicateOrderStatus = isDuplicateOrder(errorStatusCode);
    const orderId = useMemo(
        () =>
            actions
                .map((a) => {
                    if (a.actionType === ACTION_TYPES.NAVIGATE) return a?.actionData?.orderId;
                })
                .find((orderId) => !!orderId),
        [actions],
    );

    const disableDismissOfAlert = useMemo(
        () =>
            actions
                .map((a) => {
                    if (a.actionType === ACTION_TYPES.NAVIGATE) return a?.actionData?.disableDismissOfAlert;
                })
                .find((disableDismiss) => !!disableDismiss),
        [actions],
    );
    const businessLine = getBusinessLine();

    const actionName = useMemo(
        () =>
            actions
                .map((a) => {
                    if (a.actionType === ACTION_TYPES.NAVIGATE) return a?.displayName;
                })
                .find((actName) => !!actName),
        [actions],
    );

    const additionalDuplicateOrderClickParams: AdditionalDuplicateOrderClickParams = useMemo(
        () => ({
            subtitle,
            title,
            businessLine,
            orderId,
            errorStatusCode,
            ...(errorStatusCode === ORDER_TERMINAL_STATUS_CODE && { actionName }),
        }),
        [subtitle, title, businessLine, orderId, errorStatusCode, actionName],
    );

    const hideOnOverlayClick = useMemo(() => {
        switch (errorStatusCode) {
            case DINEOUT_BILL_PAYMENT_INVALID_QR_STATUS_CODE:
                return false;
            case CARD_RATE_LIMIT_EXCEEDED_STATUS_CODE:
                if (isCategoryInstamart()) {
                    return true;
                }

                return false;
            case ORDER_TERMINAL_STATUS_CODE:
                return !disableDismissOfAlert;
            default:
                return true;
        }
    }, [disableDismissOfAlert, errorStatusCode]);

    const hideCloseIcon = useMemo(() => {
        switch (errorStatusCode) {
            case DINEOUT_BILL_PAYMENT_INVALID_QR_STATUS_CODE:
                return true;
            case CARD_RATE_LIMIT_EXCEEDED_STATUS_CODE:
                if (isCategoryInstamart()) {
                    return false;
                }

                return true;
            case ORDER_TERMINAL_STATUS_CODE:
                return disableDismissOfAlert;

            default:
                return false;
        }
    }, [disableDismissOfAlert, errorStatusCode]);

    const hideIcon = useMemo(() => errorStatusCode === DINEOUT_BILL_PAYMENT_INVALID_QR_STATUS_CODE, [errorStatusCode]);

    const actionItems: PaymentAlertActionProps[] = useMemo(
        () =>
            actions.map((action: OrderInterruptionAction) => {
                return {
                    text: action.displayName,
                    subText: getActionSubtext(
                        errorStatusCode,
                        action.actionType,
                        amountToPayWithoutCouponWithoutSplitpay,
                    ),
                    onClick: (): void => {
                        handleActionClick(action, additionalDuplicateOrderClickParams);
                    },
                };
            }),
        [
            actions,
            errorStatusCode,
            amountToPayWithoutCouponWithoutSplitpay,
            handleActionClick,
            additionalDuplicateOrderClickParams,
            getActionSubtext,
        ],
    );

    useEffectOnce(() => {
        const cartId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.cartId);
        const businessLine = GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine);
        const rawCartContextForAnalytics = props?.payload?.placeOrderData?.extraArbitaryData
            ?.cartContextForAnalytics as Record<string, unknown> | undefined;
        switch (errorStatusCode) {
            case PAYMENT_MAX_RETRIES_EXCEEDED_SOFT_FAILURE_STATUS_CODE:
                actions.forEach((action) => {
                    if (
                        (action.actionData as NavigateAction["actionData"])?.screen === NavigationScreenLinkTypes.TRACK
                    ) {
                        maxPaymentRetriesAutoPaySoftFailureImpressionEvent(
                            impressionEvent,
                            IMPRESSION_ACTIONS.IMPRESSION_PAY_ON_DELIVERY_GOTO_TRACK_SCREEN,
                            billTotal,
                        );
                    } else if (action.actionType === ACTION_TYPES.DISMISS) {
                        maxPaymentRetriesAutoPaySoftFailureImpressionEvent(
                            impressionEvent,
                            IMPRESSION_ACTIONS.IMPRESSION_PAY_ON_DELIVERY_CONTINUE_PAYING,
                            billTotal,
                        );
                    }
                });

                return;
            case PAYMENT_MAX_RETRIES_EXCEEDED_STATUS_CODE:
                maxPaymentRetriesAutoPayImpressionEvent(impressionEvent, billTotal);
                return;
            case PAST_PAYMENT_PENDING_STATUS_CODE:
                pastPaymentPendingAutoPayImpressionEvent(impressionEvent, billTotal);
                return;
            case CARD_RATE_LIMIT_EXCEEDED_STATUS_CODE:
                // No impression event for this case
                return;
            case ORDER_TERMINAL_STATUS_CODE:
                recordNREvent({
                    eventType: NEWRELIC_TABLES.PAYMENT_WEB_ORDER_TERMINAL_ALERT_NAVIGATIONS,
                    orderId: orderId ?? "",
                    errorCode: errorCodes?.[0] ?? "",
                });

                // send screen view event
                terminalStateAlertScreenviewEvent(screenViewEvent, errorCodes?.[0] ?? "");

                // clearing cart on load of terminal state alert
                clearCart();
                break;
            default:
                if (isDuplicateOrderStatus) {
                    cartId &&
                        duplicateOrderDialogImpressionEvent(
                            cartId,
                            impressionEvent,
                            additionalDuplicateOrderClickParams,
                        );
                } else {
                    const cartContextForAnalytics = getCartContextForAnalytics(rawCartContextForAnalytics);
                    const params = {
                        cartId,
                        finalAmount: subtitle,
                        businessLine,
                        errorStatusCode,
                        cartContextForAnalytics,
                    };
                    cartId && surgeChangeOrderDialogImpressionEvent(params, impressionEvent);
                }
                return;
        }
    });

    if (checkoutState.type !== CheckoutActionTypes.ORDER_INTERRUPTION) {
        return null;
    }

    if (
        [
            PAYMENT_MAX_RETRIES_EXCEEDED_STATUS_CODE,
            PAST_PAYMENT_PENDING_STATUS_CODE,
            PAYMENT_MAX_RETRIES_EXCEEDED_SOFT_FAILURE_STATUS_CODE,
        ].includes(errorStatusCode)
    ) {
        return (
            <PaymentMaxRetriesAlert
                title={title}
                message={subtitle}
                info={message}
                primaryAction={actionItems[0]}
                secondaryAction={actionItems[1]}
                hideOnOverlayClick={false}
                onClose={closeHalfCard}
                actions={actions}
            />
        );
    }
    return (
        <PaymentAlert
            title={title}
            message={subtitle}
            info={message}
            primaryAction={actionItems[0]}
            secondaryAction={actionItems[1]}
            hideOnOverlayClick={hideOnOverlayClick}
            onClose={closeHalfCard}
            icon={icon}
            hideCloseIcon={hideCloseIcon}
            hideIcon={hideIcon}
        />
    );
}

export default memo(OrderInterruptionAlert);
